import { BADGE_STAT_KEYS, BADGE_STAT_LABELS, type BadgeStatKey } from 'features/catalog/badge-definitions';
import {
  badgeOf,
  battleFirstPile,
  battlePileCount,
  canActivateStat,
  cardEffects,
  cardLegalPiles,
  effectDoesNothing,
  effectRequiresDeck,
  isPileDisabled,
  legalPilesFor,
  pileCard,
  pileUnlockRound,
  totalStats,
} from 'features/battle/rules';
import {
  BATTLE_MAX_HAND,
  type BattleEffectTrigger,
  type BattleInput,
  type BattlePlayer,
  type BattleState,
  type PileIndex,
  type PlayerId,
} from 'features/battle/types';

/** Muted trigger text when it cannot run. On Reveal grays after it has already resolved. */
export const effectTriggerBlocked = (
  trigger: BattleEffectTrigger | undefined,
  leading: boolean,
  onRevealFired?: boolean
): boolean => {
  if (!trigger) return false;
  if (trigger === 'On Reveal' && onRevealFired != null) return true;
  return !leading;
};

/** Board On Reveal has already happened. A miss is Not Activated; hand and catalog stay "On Reveal". */
export const effectTriggerLabel = (
  trigger: BattleEffectTrigger | undefined,
  onRevealFired?: boolean
): string | undefined => {
  if (trigger === 'On Reveal' && onRevealFired === true) return 'Revealed';
  if (trigger === 'On Reveal' && onRevealFired === false) return 'Not Activated';
  return trigger;
};

/** On Reveal keeps its stat pill in hand, deck, and catalog; the board drops it after the reveal resolves. */
export const effectShowsStatPill = (trigger: BattleEffectTrigger | undefined, onRevealFired?: boolean): boolean =>
  trigger !== 'On Reveal' || onRevealFired == null;

export const hoverStatKeys = (
  stats: Record<BadgeStatKey, number>,
  effectStats: readonly BadgeStatKey[],
  preferStat?: BadgeStatKey
): BadgeStatKey[] => {
  const effectRank = (key: BadgeStatKey) => {
    const index = effectStats.indexOf(key);
    return index === -1 ? Number.POSITIVE_INFINITY : index;
  };
  const linked = new Set(effectStats);
  // Towers can pin a catalog filter first. An effect pill already leads, so leftover stats stay high-to-low.
  const preferRank = (key: BadgeStatKey) => (linked.size === 0 && key === preferStat ? 0 : 1);
  const effectFirst = (key: BadgeStatKey) => (linked.has(key) ? 0 : 1);
  // Zero non-effect stats stay hidden. A drained effect stat still shows, e.g. "Spotlight 0".
  return BADGE_STAT_KEYS.filter((key) => stats[key] > 0 || linked.has(key)).sort(
    (a, b) =>
      effectFirst(a) - effectFirst(b) ||
      preferRank(a) - preferRank(b) ||
      stats[b] - stats[a] ||
      effectRank(a) - effectRank(b) ||
      BADGE_STAT_KEYS.indexOf(a) - BADGE_STAT_KEYS.indexOf(b)
  );
};

/**
 * Three stats: pill and the next name stay full, the last is an initial.
 * Four or more: only the leading pill stays full so the rest fit.
 */
export const hoverStatLabel = (key: BadgeStatKey, index: number, count: number): string =>
  index >= (count >= 4 ? 1 : 2) ? BADGE_STAT_LABELS[key].slice(0, 1) : BADGE_STAT_LABELS[key];

/** Round when the rightmost remaining pile unlocks (turn 4 on a full board). */
const allPilesUnlockedRound = (state: BattleState): number =>
  pileUnlockRound((battlePileCount(state) - 1) as PileIndex);

/**
 * Piles a hand badge could occupy once every remaining pile is unlocked.
 * Hand effect pills use this so a later pile can still show a stat lead.
 */
export const handPreviewPiles = (state: BattleState, cardId: string): PileIndex[] => {
  const round = allPilesUnlockedRound(state);
  const piles = cardLegalPiles(state, cardId, round);
  if (piles.length) return piles;
  return legalPilesFor(badgeOf(state, cardId), round, battlePileCount(state), battleFirstPile(state));
};

/** Board totals with each `handPreviewPiles` slot emptied, so the hover can add this badge on top. */
export const handPreviewRevealBoards = (
  state: BattleState,
  player: PlayerId,
  cardId: string
): Record<BadgeStatKey, number>[] => {
  const seat = state.players[player];
  const piles = handPreviewPiles(state, cardId);
  const slots = piles.length ? piles : ([0, 1, 2, 3] as PileIndex[]);
  return slots.map((pile) =>
    totalStats(state, {
      ...seat,
      piles: seat.piles.map((id, i) => (i === pile ? null : id)) as BattlePlayer['piles'],
    })
  );
};

export const revealableCards = (state: BattleState): { cardId: string; piles: PileIndex[] }[] => {
  if (state.phase !== 'turn' || state.pending) return [];
  return state.players[state.activePlayer].hand.flatMap((cardId) => {
    const piles = cardLegalPiles(state, cardId, state.round);
    return piles.length ? [{ cardId, piles }] : [];
  });
};

export const playablePiles = (state: BattleState): PileIndex[] => {
  if (state.phase !== 'turn' || state.pending) return [];
  const player = state.activePlayer;
  const piles: PileIndex[] = [];
  ([0, 1, 2, 3] as PileIndex[]).forEach((pile) => {
    const cardId = pileCard(state.players[player], pile);
    if (!cardId || isPileDisabled(state, pile)) return;
    if (
      cardEffects(state, cardId).some(
        (effect) =>
          effect.stat != null &&
          effect.trigger === 'Activate' &&
          canActivateStat(state, player, effect.stat) &&
          (!effectRequiresDeck(effect.ops) || state.players[player].deck.length > 0) &&
          !effectDoesNothing(state, player, pile, effect.ops, true)
      )
    ) {
      piles.push(pile);
    }
  });
  return piles;
};

export const hasTurnAction = (state: BattleState): boolean =>
  canDraw(state) || revealableCards(state).length > 0 || playablePiles(state).length > 0;

export const canDraw = (state: BattleState): boolean => {
  const player = state.players[state.activePlayer];
  return (
    state.phase === 'turn' &&
    !state.pending &&
    state.actionsLeft > 0 &&
    player.deck.length > 0 &&
    player.hand.length < BATTLE_MAX_HAND
  );
};

export const isMyTurnToAct = (state: BattleState, me: PlayerId): boolean => {
  if (state.phase === 'game_over') return false;
  if (state.pending) return state.pending.player === me;
  return state.phase === 'turn' && state.activePlayer === me;
};

type TurnClock = Pick<BattleState, 'phase' | 'activePlayer' | 'round' | 'turnsTakenThisRound'>;

/**
 * The board on screen is still the same turn as the committed battle.
 * Opponent-move presentation keeps the previous clock for a beat, so a last action can read as
 * "1 more action" after that seat's turn has already ended.
 */
export const shownTurnStillOpen = (shown: TurnClock, committed: TurnClock): boolean =>
  shown.phase === 'turn' &&
  committed.phase === 'turn' &&
  committed.activePlayer === shown.activePlayer &&
  committed.round === shown.round &&
  committed.turnsTakenThisRound === shown.turnsTakenThisRound;

export const pendingPrompt = (state: BattleState): string => {
  const pending = state.pending;
  if (!pending) return '';
  if (pending.type === 'choose_pile') {
    if (pending.purpose === 'move') return 'Choose a badge to swap this badge with.';
    return pending.options
      ? 'Choose a pile to reveal onto, or click the badge again to pick another.'
      : 'Choose a pile to reveal onto.';
  }
  if (pending.type === 'choose_hand') {
    if (pending.purpose === 'reveal') return 'Choose a badge to reveal.';
    if (pending.purpose === 'bottom')
      return pending.count > 1
        ? `Put ${pending.count} of these badges on the bottom of your deck.`
        : 'Put one of these badges on the bottom of your deck.';
    if (pending.purpose === 'give')
      return pending.count > 1 ? `Give ${pending.count} badges.` : 'Give a badge to your opponent.';
    if (pending.count > 1) return `Discard ${pending.count} badges.`;
    const chooser = state.players[pending.player];
    return pending.cardIds.length < chooser.hand.length ? 'Discard one of the drawn badges.' : 'Discard a badge.';
  }
  if (pending.type === 'choose_board_badge') {
    if (pending.purpose === 'play_other') return 'Activate another badge on the board.';
    if (pending.purpose === 'bounce') {
      if (pending.to === 'discard') return "Choose a revealed badge on your opponent's board to discard.";
      if (pending.to === 'bottom')
        return "Choose a revealed badge on your opponent's board to return to the bottom of their deck.";
      return pending.to === 'deck'
        ? "Choose a revealed badge on your opponent's board to return to their deck."
        : "Choose a revealed badge on your opponent's board to return to their hand.";
    }
    if (pending.purpose === 'swap_source') return "Choose a badge on your opponent's board to swap with.";
    if (pending.purpose === 'discard_self') return 'Choose a revealed badge on your board to discard.';
    if (pending.purpose === 'bounce_self') {
      if (pending.who === 'opponent') {
        if (pending.to === 'bottom')
          return 'Choose a revealed badge on your board to return to the bottom of their deck.';
        return pending.to === 'deck'
          ? 'Choose a revealed badge on your board to return to their deck.'
          : 'Choose a revealed badge on your board to return to their hand.';
      }
      if (pending.to === 'bottom') return 'Choose a revealed badge on your board to return to the bottom of your deck.';
      return pending.to === 'deck'
        ? 'Choose a revealed badge on your board to return to your deck.'
        : 'Choose a revealed badge on your board to return to your hand.';
    }
    return "Choose a badge on your opponent's board.";
  }
  if (pending.type === 'choose_rarity') return 'Name a rarity.';
  if (pending.type === 'choose_deck_card') return 'Draw one of these badges. The others go under your deck.';
  if (pending.type === 'choose_stat') {
    return pending.gain ? 'Choose a stat.' : 'Choose a stat this badge does not have.';
  }
  if (pending.type === 'choose_swap_drawn') return 'Exchange the two drawn badges?';
  return '';
};

export const battleActionPrompt = (state: BattleState, me: PlayerId): string => {
  if (state.phase === 'game_over') return '';
  if (state.pending && state.pending.player !== me) {
    return `${state.players[state.pending.player].name} is choosing…`;
  }
  const prompt = pendingPrompt(state);
  return state.undo?.player === me ? `${prompt} Click the badge again to undo.` : prompt;
};

export const cpuFallbackDraw = (_state: BattleState): BattleInput => ({ type: 'draw' });
